import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { auth } from "@/lib/auth";
import { Tag, CreditCard, LogIn, Upload, Loader2, Check, FileAudio, Percent } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PACK_INFO = {
    pack1: { name: "PACK 1 — ESSENTIEL", deposit: "acompte_300", depositLabel: "300 CHF" },
    pack2: { name: "PACK 2 — SIGNATURE", deposit: "acompte_750", depositLabel: "750 CHF" },
    pack3: { name: "PACK 3 — PREMIUM", deposit: "acompte_1500", depositLabel: "1 500 CHF" },
};

export default function OfferPage() {
    const { lang } = useLang();
    const [offer] = useState(() => window.__offer || { pack: "pack2", discount: 30, from: "1 200", to: "840" });
    const [authed, setAuthed] = useState(false);
    const [paying, setPaying] = useState(false);
    const [payError, setPayError] = useState(false);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploaded, setUploaded] = useState(null);
    const fileRef = useRef(null);
    const pack = PACK_INFO[offer.pack] || PACK_INFO.pack2;

    useEffect(() => auth.subscribe((s) => setAuthed(!!s.user)), []);

    const pay = async () => {
        setPaying(true);
        setPayError(false);
        try {
            const r = await fetch(`${API}/api/payments/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    lookup_key: pack.deposit,
                    origin_url: window.location.origin,
                    booking_ref: `OFFRE −${offer.discount}% ${pack.name}`,
                }),
            });
            const d = await r.json();
            if (!r.ok || !d.checkout_url) throw new Error();
            window.location.href = d.checkout_url;
        } catch {
            setPayError(true);
            setPaying(false);
        }
    };

    const upload = async () => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("offer", `${pack.name} −${offer.discount}%`);
            const r = await auth.fetch(`${API}/api/offers/upload`, { method: "POST", body: fd });
            if (!r.ok) throw new Error();
            const d = await r.json();
            setUploaded(d.filename);
        } catch {
            setUploaded("ERR");
        } finally {
            setUploading(false);
        }
    };

    return (
        <section data-testid="offer-page" className="relative min-h-screen bg-ink px-5 pb-24 pt-32 text-cream md:px-10">
            <div className="pointer-events-none absolute inset-0 opacity-25"
                style={{ background: "radial-gradient(55% 45% at 50% 0%, rgba(212,175,55,0.22) 0%, transparent 70%)" }} />
            <div className="relative mx-auto max-w-[760px]">
                <SectionLabel en="GOLDEN ARROW — OFFER" fr="FLÈCHE D'OR — OFFRE" />
                <FadeUp delay={0.1}>
                    <div className="mt-8 text-center">
                        <p className="inline-flex items-center gap-2 border border-gold/60 bg-gold/10 px-5 py-2 font-mono text-[10px] tracking-[0.3em] text-gold">
                            <Tag size={12} /> {pack.name}
                        </p>
                        <h1 className="mt-6 font-display text-[clamp(2.4rem,8vw,5.5rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                            <span className="text-cream/40 line-through decoration-blood/70">{offer.from} CHF</span>
                            <span className="ml-4 text-gold">{offer.to} CHF</span>
                        </h1>
                        <p className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] text-blood">
                            <Percent size={13} /> −{offer.discount}% {lang === "fr" ? "OFFRE FLÈCHE D'OR" : "GOLDEN ARROW OFFER"}
                        </p>
                    </div>
                </FadeUp>

                <div className="mt-12 space-y-6">
                    {/* étape 1 — compte */}
                    <FadeUp delay={0.15}>
                        <div className="border border-white/10 bg-white/[0.03] p-6">
                            <p className="font-mono text-[9px] tracking-[0.3em] text-violet">
                                01 — {lang === "fr" ? "VOTRE COMPTE" : "YOUR ACCOUNT"}
                            </p>
                            {authed ? (
                                <p data-testid="offer-logged" className="mt-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-green-400">
                                    <Check size={13} /> {lang === "fr" ? "CONNECTÉ — VOTRE OFFRE EST LIÉE À VOTRE COMPTE" : "LOGGED IN — OFFER LINKED TO YOUR ACCOUNT"}
                                </p>
                            ) : (
                                <button
                                    data-testid="offer-login"
                                    onClick={() => window.__goPage?.("portal")}
                                    className="mt-4 flex items-center gap-2 border border-violet/60 px-6 py-3 font-mono text-[10px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream"
                                >
                                    <LogIn size={13} /> {lang === "fr" ? "SE CONNECTER / CRÉER UN COMPTE" : "SIGN IN / CREATE ACCOUNT"}
                                </button>
                            )}
                        </div>
                    </FadeUp>

                    {/* étape 2 — acompte */}
                    <FadeUp delay={0.2}>
                        <div className="border border-white/10 bg-white/[0.03] p-6">
                            <p className="font-mono text-[9px] tracking-[0.3em] text-gold">
                                02 — {lang === "fr" ? "BLOQUER L'OFFRE PAR ACOMPTE" : "LOCK THE OFFER WITH A DEPOSIT"}
                            </p>
                            <button
                                data-testid="offer-pay"
                                onClick={pay}
                                disabled={paying}
                                className="mt-4 flex w-full items-center justify-center gap-2 border border-gold bg-gold/10 py-4 font-mono text-[10px] tracking-[0.3em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-40"
                            >
                                {paying ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />}
                                {lang === "fr" ? "PAYER L'ACOMPTE" : "PAY THE DEPOSIT"} — {pack.depositLabel}
                            </button>
                            {payError && (
                                <p data-testid="offer-pay-error" className="mt-3 text-center font-mono text-[9px] tracking-[0.2em] text-blood">
                                    {lang === "fr" ? "PAIEMENT INDISPONIBLE — RÉESSAYEZ" : "PAYMENT UNAVAILABLE — RETRY"}
                                </p>
                            )}
                        </div>
                    </FadeUp>

                    {/* étape 3 — son à clipper */}
                    <FadeUp delay={0.25}>
                        <div className="border border-white/10 bg-white/[0.03] p-6">
                            <p className="font-mono text-[9px] tracking-[0.3em] text-violet">
                                03 — {lang === "fr" ? "ENVOYEZ LE SON À CLIPPER" : "SEND THE TRACK TO SHOOT"}
                            </p>
                            <div className="mt-4 flex flex-col gap-3">
                                <button
                                    data-testid="offer-upload-btn"
                                    onClick={() => fileRef.current?.click()}
                                    className="flex items-center justify-center gap-2 border border-dashed border-white/25 px-6 py-4 font-mono text-[10px] tracking-[0.25em] text-cream/60 transition-all hover:border-violet hover:text-violet"
                                >
                                    <FileAudio size={14} />
                                    {file ? file.name : (lang === "fr" ? "CHOISIR UN FICHIER AUDIO (MP3/WAV)" : "CHOOSE AN AUDIO FILE (MP3/WAV)")}
                                </button>
                                <input ref={fileRef} type="file" accept="audio/*,.mp3,.wav,.m4a" className="hidden"
                                    onChange={(e) => { setFile(e.target.files?.[0] || null); setUploaded(null); }} />
                                {file && !uploaded && (
                                    <button
                                        data-testid="offer-upload-submit"
                                        onClick={upload}
                                        disabled={uploading}
                                        className="flex items-center justify-center gap-2 border border-violet bg-violet/15 py-3 font-mono text-[10px] tracking-[0.3em] text-violet transition-all hover:bg-violet hover:text-cream disabled:opacity-40"
                                    >
                                        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                                        {lang === "fr" ? "ENVOYER LE SON" : "SEND THE TRACK"}
                                    </button>
                                )}
                                {uploaded && uploaded !== "ERR" && (
                                    <p data-testid="offer-upload-ok" className="flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.2em] text-green-400">
                                        <Check size={13} /> {lang === "fr" ? "SON REÇU — ON S'OCCUPE DU RESTE" : "TRACK RECEIVED — WE'LL HANDLE IT"}
                                    </p>
                                )}
                                {uploaded === "ERR" && (
                                    <p data-testid="offer-upload-err" className="text-center font-mono text-[9px] tracking-[0.2em] text-blood">
                                        {lang === "fr" ? "ÉCHEC — FICHIER TROP LOURD OU FORMAT NON SUPPORTÉ (MAX 30 MO)" : "FAILED — FILE TOO BIG OR UNSUPPORTED (MAX 30 MB)"}
                                    </p>
                                )}
                            </div>
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.3}>
                        <p className="text-center font-mono text-[8px] leading-relaxed tracking-[0.25em] text-cream/30">
                            {lang === "fr"
                                ? "L'OFFRE EST GELÉE 72H APRÈS PAIEMENT DE L'ACOMPTE — LE SOLDE SE RÈGLE AVANT LE TOURNAGE."
                                : "THE OFFER IS FROZEN FOR 72H AFTER DEPOSIT — THE BALANCE IS PAID BEFORE THE SHOOT."}
                        </p>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}
